import React, { useCallback, useRef, useState } from 'react';

import { Box, LinearProgress } from '@mui/material';
import { TabsCDS } from 'app/shared/componentsRestyled/coreDesignSystem/TabsCDS';
import PropTypes from 'prop-types';

import GenerateBundleFormTab from './GenerateBundleFormTab';
import { GenerateBundleModalTestIds } from './GenerateBundleModal.testIds';
import MissingDocumentTypesTab from './MissingDocumentTypesTab';

const GENERATE_FORM_TAB = 'generateForm';
const MISSING_DOCUMENTS_TAB = 'missingDocumentTypes';

const baseTabsConfig = [{ label: 'Generate Bundle', value: GENERATE_FORM_TAB }];

const missingDocumentsTabConfig = { label: 'Missing Document Types', value: MISSING_DOCUMENTS_TAB };

const initialGenerateBundleState = {
  isLoading: false,
  hasNoDocuments: false,
  hasMissingDocuments: false,
  ignoreMissingDocuments: false,
  missingDocuments: [],
  error: '',
  bundleType: '',
};

const generateBundleModalStyles = {
  width: '800px',
};

const GenerateBundleModal = ({
  documentBundleTypes,
  loanParams,
  abortController,
  formRef = null,
}) => {
  const isUseEffect = useRef(true);

  const [value, setValue] = useState(GENERATE_FORM_TAB);
  const [chosenMissingDocumentTypeId, setChosenMissingDocumentTypeId] = useState('');
  const [
    {
      isLoading,
      hasNoDocuments,
      hasMissingDocuments,
      missingDocuments,
      error,
      bundleType,
      ignoreMissingDocuments,
    },
    setGenerateBundleState,
  ] = useState(initialGenerateBundleState);

  const onClickTab = useCallback((e, tabValue) => {
    setValue(tabValue);
  }, []);

  const hasMissingDocumentsTab = missingDocuments.length > 0;
  const tabs = hasMissingDocumentsTab
    ? [...baseTabsConfig, missingDocumentsTabConfig]
    : baseTabsConfig;

  // If missingDocuments empties while the missing-docs tab is active, fall back
  // to the first tab so the UI doesn't point at a removed tab.
  const activeValue =
    !hasMissingDocumentsTab && value === MISSING_DOCUMENTS_TAB ? GENERATE_FORM_TAB : value;

  return (
    <Box sx={generateBundleModalStyles}>
      <LinearProgress className={isLoading ? 'visible' : 'invisible'} color="primary" />
      <TabsCDS
        data-testid={GenerateBundleModalTestIds.TABS}
        tabs={tabs}
        value={activeValue}
        onChange={onClickTab}
      />
      {activeValue === GENERATE_FORM_TAB && (
        <GenerateBundleFormTab
          bundleType={bundleType}
          documentBundleTypes={documentBundleTypes}
          error={error}
          formRef={formRef}
          hasMissingDocuments={hasMissingDocuments}
          hasNoDocuments={hasNoDocuments}
          ignoreMissingDocuments={ignoreMissingDocuments}
          initialGenerateBundleState={initialGenerateBundleState}
          isLoading={isLoading}
          isUseEffect={isUseEffect}
          loanParams={loanParams}
          setChosenMissingDocumentTypeId={setChosenMissingDocumentTypeId}
          setGenerateBundleState={setGenerateBundleState}
        />
      )}
      {activeValue === MISSING_DOCUMENTS_TAB && (
        <MissingDocumentTypesTab
          abortController={abortController}
          chosenMissingDocumentTypeId={chosenMissingDocumentTypeId}
          formRef={formRef}
          loanParams={loanParams}
          missingDocuments={missingDocuments}
          setChosenMissingDocumentTypeId={setChosenMissingDocumentTypeId}
          setGenerateBundleState={setGenerateBundleState}
        />
      )}
    </Box>
  );
};

GenerateBundleModal.propTypes = {
  documentBundleTypes: PropTypes.array.isRequired,
  abortController: PropTypes.object.isRequired,
  loanParams: PropTypes.object.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }),
};

export default GenerateBundleModal;
